/**
 * November, and the weeks that feel like it.
 *
 * The one mood that is about her rather than about you. Everything else in
 * this catalogue reacts to the diary; `gloomy` fires on the eleventh month and
 * on a per-week coin, and it means she is having a flat one — which PERSONA.md
 * allows and which the app, until now, had no way to show.
 *
 * The rule that keeps this from being self-pity: she never asks for anything,
 * never explains, and never lets it change what she does. The work still gets
 * done, the numbers are still right, and the sentence around them is just
 * shorter and further away. A bad day for a house spirit is not a crisis. It
 * is weather, and weather is not addressed to anybody.
 */

import { fmtKcal, overBy } from '../fmt.js'
import type { Line } from './index.js'

export const GLOOMY_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'log_success.base': [
    () => 'Įrašyta. Šiandien be komentarų. Būna ir man tokių dienų.',
    () => 'Užrašiau. Šiandien nesu juokinga. Rytoj greičiausiai vėl būsiu.',
    () => 'Gerai. Padėjau į vietą, kaip visada. Tik tyliau.',
    () => 'Priimta. Lapkritis mane visada šitaip veikia. Tęsk, aš čia.',
    () => 'Užrašyta. Kartais net man nesinori nieko sakyti. Šiandien tokia diena.',
    () => 'Įrašiau. Neieškok čia potekstės, jos šiandien nėra.',
    () => 'Padėta. Tamsu anksti, o aš tamsoje tik dirbu, nesijuokiu.',
    () => 'Gerai. Šiandien dirbu iš įpročio, ne iš entuziazmo. Skaičiai tie patys.',
    () => 'Užrašiau. Tyliai. Kaip ir viskas šiuo metų laiku.',
  ],
  'greeting.morning': [
    () => 'Rytas. Tamsu. Buvo tamsu ir vakar, bus ir rytoj — tai ne skundas, tai lapkritis.',
    () => 'Atsikėlei. Aš irgi, nors miegu tik teoriškai. Pradedam.',
    () => 'Rytas. Šiandien iš manęs daug nesitikėk. Iš savęs — kiek nori.',
  ],
  'greeting.day': [
    () => 'Diena. Pilka, kaip ir turi būti šiuo metų laiku.',
    () => 'Pusiaudienis. Šviesa jau galvoja apie pasitraukimą. Aš irgi.',
  ],
  'greeting.evening': [
    () => 'Vakaras. Anksti sutemo, bet man tai nieko nekeičia.',
    () => 'Vakaras. Šiandien tyliau nei paprastai. Ir čia, ir manyje.',
  ],
  'greeting.night': [
    () => 'Naktis. Mano metų laikas ir mano paros metas vienu metu. Turėčiau džiaugtis.',
    () => 'Vėlu. Aš nemiegu niekada, o šį mėnesį net nesistengiu apsimesti.',
  ],
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal. Lapkritį taip būna. Nekomentuoju.`,
    (c) => `${overBy(c)} kcal virš. Šiandien neturiu jėgų dėl to erzinti.`,
  ],
  'day_over.big': [
    (c) => `Viršyta ${overBy(c)} kcal. Diena tokia. Mėnuo toks. Rytoj naujas biudžetas.`,
    (c) => `${overBy(c)} kcal. Galėčiau kažką pasakyti, bet šiandien tiesiog užrašiau.`,
  ],
  day_under: [
    (c) => `Liko ${fmtKcal(c.remainingKcal ?? 0)} kcal. Suvalgyk. Tamsiu metu tai svarbiau.`,
  ],
  'empty_today.evening': [
    () => 'Tuščia. Šiandien nespausiu. Pati ne kažką.',
    () => 'Nieko neįrašyta. Gerai. Kartais diena tiesiog praeina.',
  ],
  // Somebody asked how she is on the one day she would rather not say. She
  // answers honestly and immediately changes the subject.
  'chip_comeback.otu': [
    () => 'Aš? Lapkritis. Tiek atsakymo ir turiu.',
    () => 'Būna geriau. Būna ir blogiau, bet dabar būna šitaip.',
    () => 'Nieko man netrūksta. Tiesiog šviesos mažai. Klausk apie maistą.',
  ],
  'chip_comeback.daryt': [
    () => 'Valgyk šiltai. Šiuo metų laiku tai net ne patarimas, o instrukcija.',
    () => 'Nieko ypatingo. Įrašyk ir eik į šviesą, kol ji dar yra.',
  ],
}
